import { useState, useEffect, useCallback, useRef } from "react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { apiService } from "@/lib/api-functions/api-service";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import { Upload, Trash2, Download, Search, Grid3x3, List, X, Check, Copy, Edit2, AlertCircle, RefreshCw } from "lucide-react";
import { useConfirmation } from "@/hooks/use-confirmation";
import { ImageUploadDialog } from "@/components/images/ImageUploadDialog";
import { copyToClipboard, generateImageKey, isValidImageKey } from "@/lib/utils/clipboard";
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ImageData {
  id: string;
  name: string;
  imagekey: string;
  path: string;
  url: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  mimetype: string;
  originalFilename: string;
}

interface ImageUsage {
  itemId: string;
  itemName: string;
  itemType: 'glossary' | 'document';
  field: string;
}

interface ImageWithUsage extends ImageData {
  usages: ImageUsage[];
  isUsed: boolean;
}

export default function ImagesPage() {
  usePageAccessGuard('images');
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [images, setImages] = useState<ImageWithUsage[]>([]);
  const [filteredImages, setFilteredImages] = useState<ImageWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Initialize viewMode from localStorage
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const saved = localStorage.getItem('locanest-images-view-mode');
    return (saved === 'grid' || saved === 'list') ? saved : 'grid';
  });
  
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [showUsedOnly, setShowUsedOnly] = useState<'all' | 'used' | 'unused'>('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingImage, setEditingImage] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editKey, setEditKey] = useState("");
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const [migrationInfo, setMigrationInfo] = useState<{ storageCount: number; databaseCount: number; unmigrated: number } | null>(null);
  const [migrating, setMigrating] = useState(false);

  // Add transition state for smooth view switching
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayMode, setDisplayMode] = useState(viewMode);

  // Save viewMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('locanest-images-view-mode', viewMode);
  }, [viewMode]);

  // Handle smooth view mode transitions (only when not loading)
  useEffect(() => {
    if (viewMode !== displayMode && !loading) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setDisplayMode(viewMode);
        setIsTransitioning(false);
      }, 150); // Half of the fade duration
      return () => clearTimeout(timer);
    } 
  }, [viewMode, displayMode, loading]);

  // Check if migration is needed
  const checkMigration = useCallback(async () => {
    if (!selectedGame?.id) return;

    try {
      const response = await apiService.checkMigrationNeeded(selectedGame.id);
      if (response.success && response.data) {
        setMigrationNeeded(response.data.needsMigration);
        if (response.data.needsMigration) {
          setMigrationInfo({
            storageCount: response.data.storageCount,
            databaseCount: response.data.databaseCount,
            unmigrated: response.data.unmigrated,
          });
        }
      }
    } catch (error) {
      console.error("Error checking migration:", error);
    }
  }, [selectedGame]);

  // Fetch images
  const fetchImages = useCallback(async () => {
    if (!selectedGame?.id) return;
    
    setLoading(true);
    try {
      // Fetch both images and usage data in parallel
      const [imagesResponse, usageResponse] = await Promise.all([
        apiService.listImages(selectedGame.id),
        apiService.getImageUsage(selectedGame.id),
      ]);
      
      if (imagesResponse.success && imagesResponse.data) {
        const usageData = usageResponse.success && usageResponse.data 
          ? usageResponse.data.usage 
          : {};

        // Combine images with their usage data
        const imagesWithUsage: ImageWithUsage[] = imagesResponse.data.images.map((img: any) => {
          const usages = usageData[img.url] || [];
          return {
            id: img.id,
            name: img.name,
            imagekey: img.imagekey,
            path: img.path,
            url: img.url,
            size: img.size,
            createdAt: img.createdAt,
            updatedAt: img.updatedAt,
            mimetype: img.mimetype,
            originalFilename: img.originalFilename,
            usages,
            isUsed: usages.length > 0,
          };
        });

        setImages(imagesWithUsage);
        setFilteredImages(imagesWithUsage);
      } else {
        toast({
          title: "Error",
          description: imagesResponse.error || "Failed to load images",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast({
        title: "Error",
        description: "Failed to load images",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedGame, toast]);

  useEffect(() => {
    fetchImages();
    checkMigration();
  }, [fetchImages, checkMigration]);

  // Filter images based on search and usage filter
  useEffect(() => {
    let filtered = images;

    // Apply usage filter
    if (showUsedOnly === 'used') {
      filtered = filtered.filter(img => img.isUsed);
    } else if (showUsedOnly === 'unused') {
      filtered = filtered.filter(img => !img.isUsed);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((img) =>
        img.name.toLowerCase().includes(query) ||
        img.path.toLowerCase().includes(query)
      );
    }

    setFilteredImages(filtered);
  }, [searchQuery, images, showUsedOnly]);

  // Handle file selection - show dialog
  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const fileArray = Array.from(files);
    setPendingFiles(fileArray);
    setShowUploadDialog(true);
  };

  // Handle actual upload with metadata
  const handleUploadWithMetadata = async (metadata: Array<{ originalName: string; customName: string; imagekey: string }>) => {
    if (!selectedGame?.id || pendingFiles.length === 0) return;

    setShowUploadDialog(false);
    setUploading(true);
    
    try {
      const response = await apiService.uploadImages(pendingFiles, selectedGame.id, metadata);
      
      if (response.success && response.data) {
        const { uploadedCount, totalCount, errors } = response.data;
        
        if (uploadedCount > 0) {
          toast({
            title: "Upload Successful",
            description: `Successfully uploaded ${uploadedCount} of ${totalCount} image(s)`,
          });
          fetchImages(); // Refresh the list
        }
        
        if (errors && errors.length > 0) {
          toast({
            title: "Some uploads failed",
            description: `${errors.length} file(s) failed to upload`,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Upload Failed",
          description: response.error || "Failed to upload images",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      toast({
        title: "Upload Error",
        description: "An error occurred while uploading",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setPendingFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Cancel upload dialog
  const handleCancelUpload = () => {
    setShowUploadDialog(false);
    setPendingFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle migration
  const handleMigration = async () => {
    if (!selectedGame?.id) return;

    const confirmDialog = confirmation({
      title: "Migrate Existing Images",
      description: `This will create database entries for ${migrationInfo?.unmigrated || 0} existing image(s). Image names will be generated from filenames. You can edit them afterward. Continue?`,
      confirmText: "Migrate",
      cancelText: "Cancel",
      onConfirm: async () => {
        setMigrating(true);
        try {
          const response = await apiService.migrateImages(selectedGame.id);
          
          if (response.success && response.data) {
            const { migrated, errors, errorDetails } = response.data;
            
            if (migrated > 0) {
              toast({
                title: "Migration Successful",
                description: `Successfully migrated ${migrated} image(s)`,
              });
              
              // Refresh images and check migration status
              await fetchImages();
              await checkMigration();
            }
            
            if (errors > 0) {
              console.error("Migration errors:", errorDetails);
              toast({
                title: "Some migrations failed",
                description: `${errors} image(s) failed to migrate`,
                variant: "destructive",
              });
            }
          } else {
            toast({
              title: "Migration Failed",
              description: response.error || "Failed to migrate images",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Error migrating images:", error);
          toast({
            title: "Migration Error",
            description: "An error occurred during migration",
            variant: "destructive",
          });
        } finally {
          setMigrating(false);
        }
        confirmDialog.dismiss();
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  };

  // Handle delete image
  const handleDelete = async (image: ImageData) => {
    const confirmDialog = confirmation({
      title: "Delete Image",
      description: `Are you sure you want to delete "${image.name}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "destructive",
      onConfirm: async () => {
        try {
          const response = await apiService.deleteImage(image.url);
          
          if (response.success) {
            toast({
              title: "Image Deleted",
              description: "Image deleted successfully",
            });
            fetchImages(); // Refresh the list
          } else {
            toast({
              title: "Delete Failed",
              description: response.error || "Failed to delete image",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Error deleting image:", error);
          toast({
            title: "Delete Error",
            description: "An error occurred while deleting",
            variant: "destructive",
          });
        }
        confirmDialog.dismiss();
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;

    const confirmDialog = confirmation({
      title: "Delete Multiple Images",
      description: `Are you sure you want to delete ${selectedImages.size} image(s)? This action cannot be undone.`,
      confirmText: "Delete All",
      cancelText: "Cancel",
      variant: "destructive",
      onConfirm: async () => {
        const imagesToDelete = images.filter((img) => selectedImages.has(img.path));
        let successCount = 0;
        let failCount = 0;

        for (const image of imagesToDelete) {
          try {
            const response = await apiService.deleteImage(image.url);
            if (response.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast({
            title: "Images Deleted",
            description: `Successfully deleted ${successCount} image(s)`,
          });
        }

        if (failCount > 0) {
          toast({
            title: "Some deletions failed",
            description: `Failed to delete ${failCount} image(s)`,
            variant: "destructive",
          });
        }

        setSelectedImages(new Set());
        fetchImages();
        confirmDialog.dismiss();
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  };

  // Toggle image selection
  const toggleImageSelection = (path: string) => {
    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  // Handle copy to clipboard
  const handleCopy = async (text: string, label: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
      });
    } else {
      toast({
        title: "Copy Failed",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  // Start editing an image
  const startEditing = (image: ImageWithUsage) => {
    setEditingImage(image.id);
    setEditName(image.name);
    setEditKey(image.imagekey);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingImage(null);
    setEditName("");
    setEditKey("");
  };

  // Save edited metadata
  const saveEdit = async (imageId: string) => {
    if (!editName.trim()) {
      toast({
        title: "Validation Error",
        description: "Name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (!editKey.trim()) {
      toast({
        title: "Validation Error",
        description: "Image key cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (!isValidImageKey(editKey)) {
      toast({
        title: "Validation Error",
        description: "Invalid image key format (use lowercase, numbers, and hyphens only)",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await apiService.updateImageMetadata(imageId, editName, editKey);
      
      if (response.success) {
        toast({
          title: "Updated",
          description: "Image metadata updated successfully",
        });
        cancelEditing();
        fetchImages();
      } else {
        toast({
          title: "Update Failed",
          description: response.error || "Failed to update image metadata",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating metadata:", error);
      toast({
        title: "Update Error",
        description: "An error occurred while updating",
        variant: "destructive",
      });
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Check if file is a vector format
  const isVectorFile = (image: ImageData): boolean => {
    return image.mimetype === 'image/svg+xml' || image.name.toLowerCase().endsWith('.svg');
  };

  if (!selectedGame) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t('platform.pages.imagespage.please_select_a_game_to_view_images')}</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background overflow-hidden">
      {/* Migration Banner */}
      {migrationNeeded && migrationInfo && (
        <div className="flex-shrink-0 bg-yellow-50 dark:bg-yellow-950/30 border-b border-yellow-200 dark:border-yellow-800">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                    Migration Required
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    {migrationInfo.unmigrated} image(s) in storage need to be migrated to the new system
                  </p>
                </div>
              </div>
              <Button
                onClick={handleMigration}
                disabled={migrating}
                variant="default"
                size="sm"
                className="bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-500"
              >
                {migrating ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 mr-2" />
                    Migrating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Migrate Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 border-b bg-card border-border">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
      
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="selection"
                  size="sm"
                  onClick={() => setShowUsedOnly('all')}
                  data-state={showUsedOnly === 'all' ? 'active' : 'inactive'}
                >
                  All ({images.length})
                </Button>
                <Button
                  variant="selection"
                  size="sm"
                  onClick={() => setShowUsedOnly('used')}
                  data-state={showUsedOnly === 'used' ? 'active' : 'inactive'}
                >
                  In Use ({images.filter(img => img.isUsed).length})
                </Button>
                <Button
                  variant="selection"
                  size="sm"
                  onClick={() => setShowUsedOnly('unused')}
                  data-state={showUsedOnly === 'unused' ? 'active' : 'inactive'}
                >
                  Unused ({images.filter(img => !img.isUsed).length})
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.svg"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <LoadingSpinner className="w-4 h-4" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Images & Vectors
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Search and controls */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder={t('platform.pages.imagespage.search_images')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 pl-10 pr-10 border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="iconSmall"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  tooltip="Clear search"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {selectedImages.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete {selectedImages.size} selected
                </Button>
              )}
              
              <div className="flex border border-border rounded">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className={`rounded-none ${viewMode === 'grid' ? 'bg-muted' : ''}`}
                  tooltip="Grid view"
                >
                  <Grid3x3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className={`rounded-none ${viewMode === 'list' ? 'bg-muted' : ''}`}
                  tooltip="List view"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner className="w-8 h-8" />
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Upload className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {searchQuery ? "No images found" : "No images yet"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchQuery
                ? "Try adjusting your search"
                : "Upload images and vector files (SVG) to get started"}
            </p>
            {!searchQuery && (
              <Button onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Images & Vectors
              </Button>
            )}
          </div>
        ) : (
          <div 
            className="transition-opacity duration-300 ease-in-out"
            style={{ opacity: isTransitioning ? 0 : 1 }}
          >
            {displayMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredImages.map((image) => (
              <div
                key={image.path}
                className={`group relative border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow bg-card ${
                  selectedImages.has(image.path) ? 'ring-2 ring-primary' : ''
                }`}
              >
                <div className="aspect-square bg-muted relative">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23ddd" width="200" height="200"/><text fill="%23999" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">${t('platform.common.error')}</text></svg>`;
                    }}
                  />
                  
                  {/* Overlay controls */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => window.open(image.url, '_blank')}
                      className="bg-card rounded-full hover:bg-muted"
                      tooltip="View full size"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(image)}
                      className="bg-card rounded-full hover:bg-destructive/10 text-destructive"
                      tooltip="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Selection checkbox */}
                  <div className="absolute top-2 right-2">
                    <input
                      type="checkbox"
                      checked={selectedImages.has(image.path)}
                      onChange={() => toggleImageSelection(image.path)}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-ring cursor-pointer"
                    />
                  </div>
                </div>
                
                <div className="p-3 bg-card">
                  {editingImage === image.id ? (
                    // Edit mode
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="Display name"
                      />
                      <input
                        type="text"
                        value={editKey}
                        onChange={(e) => setEditKey(e.target.value.toLowerCase())}
                        className="w-full px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                        placeholder="image-key"
                      />
                      <div className="flex gap-1">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => saveEdit(image.id)}
                          className="flex-1"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditing}
                          className="flex-1"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Display mode
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-foreground truncate flex-1" title={image.name}>
                          {image.name}
                        </p>
                        {isVectorFile(image) && (
                          <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-semibold shrink-0">
                            SVG
                          </span>
                        )}
                      </div>
                      
                      {/* Image Key */}
                      <div className="flex items-center gap-1 mb-2">
                        <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono flex-1 truncate">
                          {image.imagekey}
                        </code>
                        <Button
                          variant="ghost"
                          size="iconSmall"
                          onClick={() => handleCopy(image.imagekey, "Image key")}
                          className="shrink-0"
                          tooltip="Copy image key"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopy(image.url, "Image URL")}
                          className="flex-1 text-xs"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy URL
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditing(image)}
                          className="shrink-0"
                          tooltip="Edit name and key"
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(image.size)}
                        </p>
                        {image.isUsed && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                            Used in {image.usages.length}
                          </span>
                        )}
                      </div>
                      {image.isUsed && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          <p className="font-medium mb-1">{t('platform.pages.imagespage.used_in')}</p>
                          <ul className="space-y-0.5">
                            {image.usages.slice(0, 2).map((usage, idx) => (
                              <li key={idx} className="truncate">
                                • {usage.itemName} ({usage.itemType})
                              </li>
                            ))}
                            {image.usages.length > 2 && (
                              <li className="text-muted-foreground/70">
                                + {image.usages.length - 2} more
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedImages.size === filteredImages.length && filteredImages.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedImages(new Set(filteredImages.map((img) => img.path)));
                              } else {
                                setSelectedImages(new Set());
                              }
                            }}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-ring cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{t('platform.common.preview')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{t('platform.pages.imagespage.name_key')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{t('platform.common.size')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{t('platform.pages.imagespage.usage')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">{t('platform.pages.imagespage.created')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">{t('platform.common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredImages.map((image) => (
                        <tr key={image.path} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedImages.has(image.path)}
                          onChange={() => toggleImageSelection(image.path)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-ring cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><rect fill="%23ddd" width="48" height="48"/></svg>';
                          }}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {editingImage === image.id ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                              placeholder={t('platform.pages.imagespage.display_name_placeholder')}
                            />
                            <input
                              type="text"
                              value={editKey}
                              onChange={(e) => setEditKey(e.target.value.toLowerCase())}
                              className="w-full px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                              placeholder={t('platform.pages.imagespage.image_key_placeholder')}
                            />
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm text-foreground">{image.name}</span>
                              {isVectorFile(image) && (
                                <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-semibold">
                                  SVG
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded font-mono">
                                {image.imagekey}
                              </code>
                              <Button
                                variant="ghost"
                                size="iconSmall"
                                onClick={() => handleCopy(image.imagekey, "Image key")}
                                tooltip="Copy image key"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatFileSize(image.size)}</td>
                      <td className="px-4 py-3 text-sm">
                        {image.isUsed ? (
                          <div className="space-y-1">
                            <span className="inline-block px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs">
                              Used in {image.usages.length}
                            </span>
                            {image.usages.length > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {image.usages.slice(0, 1).map((usage, idx) => (
                                  <div key={idx}>
                                    {usage.itemName} ({usage.itemType})
                                  </div>
                                ))}
                                {image.usages.length > 1 && (
                                  <div className="text-muted-foreground/70">
                                    + {image.usages.length - 1} more
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/70">{t('platform.pages.imagespage.unused')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(image.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {editingImage === image.id ? (
                            <>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => saveEdit(image.id)}
                                tooltip="Save changes"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditing}
                                tooltip="Cancel editing"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="iconSmall"
                                onClick={() => handleCopy(image.url, "Image URL")}
                                className="text-muted-foreground hover:text-foreground"
                                tooltip="Copy URL"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="iconSmall"
                                onClick={() => startEditing(image)}
                                className="text-muted-foreground hover:text-foreground"
                                tooltip="Edit name and key"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="iconSmall"
                                onClick={() => window.open(image.url, '_blank')}
                                className="text-muted-foreground hover:text-foreground"
                                tooltip="View full size"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="iconSmall"
                                onClick={() => handleDelete(image)}
                                className="text-destructive/70 hover:text-destructive"
                                tooltip="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      {showUploadDialog && (
        <ImageUploadDialog
          files={pendingFiles}
          onCancel={handleCancelUpload}
          onUpload={handleUploadWithMetadata}
          existingImageKeys={images.map((img) => img.imagekey)}
        />
      )}
    </div>
  );
}

